// ADVENTURE ROAD — boot. ORCHESTRATOR-OWNED.
//
// The engine kit owns the frozen 16-step lifecycle (@engine/core createApp — device → tier →
// AppOptions → init → assertSystemsRegistered → module init → SCENE BUILD → platform → start →
// warmup render → context-loss guard → ready). Everything a scene needs must therefore be BUILT
// AND PRIMED inside its builder: the warmup render right after is the shader census, and no
// program may be created after it (the never-recompile law, docs/recon/laws-and-taste.md).
//
// installQa() runs FIRST, before any await, so window.__qa exists for the harness even if the
// scene build throws (the QA driver reports the failure instead of hanging — qa.ts contract pt 6).
//
// Scenes:
//   game     (default) the run: class select → six worlds → final boss → victory
//   herolab  the hero/gear fidelity lab (?clip= ?pose= ?gear= ?fire=)
//   audiolab the procedural audio board
// Select with ?scene=<name>; the QA harness adds ?qa=1&scenario=<name>&seed=<n>.

import {
  CameraComponentSystem,
  LightComponentSystem,
  RenderComponentSystem,
} from 'playcanvas';
import { createApp, defineGame, SceneRegistry } from '@engine/core';
import { webAdapter } from '@engine/platform';
import { installQa } from './game/qa/qa';

// window.__qa lands synchronously at module evaluation (before the async boot below).
const qa = installQa();

const scenes = new SceneRegistry('game');
// Scene modules are dynamically imported so a lab scene never ships in the game's boot path
// (and a lab-only compile error can never break the run).
scenes.register('game', async (app) => {
  const { buildGameScene } = await import('./game/scenes/gameScene');
  await buildGameScene(app, qa);
});
scenes.register('herolab', async (app) => {
  const { buildHeroLabScene } = await import('./game/scenes/heroLab');
  await buildHeroLabScene(app);
});
scenes.register('audiolab', async (app) => {
  const { buildAudioLabScene } = await import('./game/scenes/audioLab');
  await buildAudioLabScene(app);
});

const game = defineGame({
  canvas: 'app',
  modules: [{
    componentSystems: [RenderComponentSystem, CameraComponentSystem, LightComponentSystem],
    resourceHandlers: [],
  }],
  scenes,
  defaultScene: 'game',
  requiredSystems: ['render', 'camera', 'light'],
  platform: webAdapter,
  tier: {
    // Every world/FX/hero material rides custom GLSL chunks → WebGPU is not eligible (gate-14).
    contentWebgpuEligible: false,
    force: new URLSearchParams(location.search).get('gfx'),
    // MSAA is device-creation state (never a runtime knob); the post stack resolves its own
    // 4× MSAA HDR target, so the backbuffer itself does not need it.
    antialias: false,
  },
  hooks: {
    onReady: ({ app }) => {
      (globalThis as unknown as { __app: unknown }).__app = app;
    },
  },
});

await createApp(game);
